import cx from 'classnames';
import { AnchorHTMLAttributes, PropsWithChildren } from 'react';

import { Typography } from '../typography/Typography';

import type { Scale } from '../typography/Typography';

export type AnchorProps = PropsWithChildren<
    AnchorHTMLAttributes<HTMLAnchorElement> & {
        obfuscate?: boolean;
        href: string;
        /**
         * ADR-0016: shortcut reserved for a block link (the link is the whole
         * text content of its slot). Omitted, the typography is inherited from
         * the container — the only form allowed for an inline link.
         */
        scale?: Scale;
    }
>;

export function Anchor({ className, obfuscate, scale, ...props }: AnchorProps) {
    const HTMLTag = obfuscate ? 'span' : 'a';

    const link = (
        <HTMLTag
            className={cx('link', className)}
            {...props}
            href={obfuscate ? undefined : props.href}
            onClick={(event) => {
                if (typeof props.onClick === 'function') {
                    props.onClick(event);
                }

                if (obfuscate) {
                    if (props.target === '_blank' || event.ctrlKey) {
                        return window.open(props.href, '_blank', 'noopener');
                    }
                    window.location.assign(props.href);
                }
            }}
        >
            {props.children}
        </HTMLTag>
    );

    if (scale === undefined) {
        return link;
    }

    // ADR-0016: the scale class goes on a wrapper, never on the link element —
    // it has the same specificity as `.link`, so sharing an element would let
    // bundle order decide the font weight.
    return (
        <Typography as="span" scale={scale}>
            {link}
        </Typography>
    );
}
